<template>
  <aside class="navigation-rail">
    <button type="button" class="rail-avatar rail-avatar-button" :title="language.t('nav.editProfile')" @click="openProfileEditor">
      <img v-if="session.portrait" :src="session.portrait" alt="">
      <span v-else>{{ initials }}</span>
    </button>
    <button class="rail-button" :class="{ active: navigation.section === 'messages' }" :title="language.t('nav.messages')" @click="navigation.showMessages">💬</button>
    <button class="rail-button rail-contact-button" :class="{ active: navigation.section === 'contacts' }" :title="language.t('nav.contacts')" @click="showContacts">
      <span aria-hidden="true">👥</span>
      <span v-if="contacts.pendingApplicationCount" class="rail-notification-badge">
        {{ contacts.pendingApplicationCount > 99 ? '99+' : contacts.pendingApplicationCount }}
      </span>
    </button>
    <button class="rail-button" :class="{ active: navigation.section === 'favorites' }" :title="language.t('nav.favorites')" @click="navigation.showFavorites">☆</button>
    <div class="rail-spacer"></div>
    <button class="rail-button" :title="language.t('nav.settings')" :aria-label="language.t('nav.settings')" @click="openSettings">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 8.2a3.8 3.8 0 1 1 0 7.6 3.8 3.8 0 0 1 0-7.6Z"></path>
        <path d="M19.4 13.6a7.8 7.8 0 0 0 .1-1.6l1.7-1.3-1.7-3-2 .8a8 8 0 0 0-1.4-.8L15.8 5h-3.5l-.3 2.1a8 8 0 0 0-1.5.8l-1.9-.8-1.8 3 1.7 1.3a7.8 7.8 0 0 0 0 1.6l-1.7 1.3 1.8 3 1.9-.8c.5.4 1 .7 1.5.8l.3 2.1h3.5l.3-2.1c.5-.2 1-.5 1.4-.8l2 .8 1.7-3-1.8-1.3Z"></path>
      </svg>
    </button>
    <button class="rail-button rail-logout-button" :title="language.t('nav.logout')" :aria-label="language.t('nav.logout')" @click="logout">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4.5 20.5V3.5h9.2a1.8 1.8 0 0 1 1.8 1.8v13.4a1.8 1.8 0 0 1-1.8 1.8H4.5Z"></path>
        <path d="M9 12h9.5"></path>
        <path d="m15.5 8.8 3.2 3.2-3.2 3.2"></path>
        <path d="M7.8 20.5V5.8l6.5-2.3"></path>
      </svg>
    </button>
  </aside>

  <div v-if="settingsOpen" class="modal-backdrop settings-backdrop" @click.self="closeSettings">
    <section class="settings-panel">
      <header>
        <strong>{{ language.t('settings.title') }}</strong>
        <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeSettings">×</button>
      </header>
      <div class="settings-layout">
        <nav v-if="!settingsSection" class="settings-menu" :aria-label="language.t('settings.menu')">
          <button
            v-for="item in settingsItems"
            :key="item.key"
            type="button"
            @click="selectSettingsSection(item.key)"
          >
            <span class="settings-menu-copy">
              <strong>{{ item.label }}</strong>
              <small v-if="item.comingSoon">{{ language.t('settings.comingSoon') }}</small>
            </span>
            <span class="settings-menu-chevron" aria-hidden="true">›</span>
          </button>
          <DesktopInstall />
        </nav>

        <div v-else class="settings-detail">
          <button type="button" class="settings-back-button" :aria-label="language.t('botMother.back')" @click="showSettingsMenu">
            <span aria-hidden="true">←</span>
            <span>{{ language.t('settings.title') }}</span>
          </button>

        <form v-if="settingsSection === 'password'" class="settings-password-form" @submit.prevent="savePassword">
          <h3>{{ language.t('settings.changePassword') }}</h3>
          <label>
            <span>{{ language.t('settings.currentPassword') }}</span>
            <input v-model="passwordDraft.oldPassword" type="password" autocomplete="current-password">
          </label>
          <label>
            <span>{{ language.t('settings.newPassword') }}</span>
            <input v-model="passwordDraft.newPassword" type="password" autocomplete="new-password">
          </label>
          <label>
            <span>{{ language.t('settings.confirmNewPassword') }}</span>
            <input v-model="passwordDraft.confirmPassword" type="password" autocomplete="new-password">
          </label>
          <footer>
            <span v-if="settingsNotice" class="inline-save-status">{{ language.t(settingsNotice) }}</span>
            <span v-else-if="settingsError" class="inline-save-status error">{{ language.t(settingsError) }}</span>
            <button type="submit" class="primary-inline-action" :disabled="passwordSaving">
              {{ passwordSaving ? language.t('common.saving') : language.t('settings.savePassword') }}
            </button>
          </footer>
        </form>

        <section v-else-if="settingsSection === 'nlc'" class="settings-nlc-panel">
          <div v-if="nlcRestrictionCode === 'NLC_ADMIN_BANNED'" class="settings-nlc-restriction-inline" role="alert">
            <span>{{ language.t('settings.nlcBannedPrefix') }}</span>
            <button type="button" @click="openOfficialSupport">{{ language.t('settings.officialSupport') }}</button>
          </div>
          <template v-else>
          <div class="settings-nlc-heading">
            <div class="settings-nlc-titlebar">
              <div><span class="settings-nlc-brand">NLC · Native Language Chat</span><h3>{{ language.t('settings.nlcTitle') }}</h3></div>
              <button type="button" class="settings-nlc-plan-badge" :disabled="nlcLoading" @click="selectSettingsSection('nlc-plans')">{{ language.t(`nlcPlans.${currentNlcPlan}`) }}</button>
            </div>
            <p>{{ language.t('settings.nlcIntro') }}</p>
          </div>

          <label class="settings-nlc-switch">
            <span>
              <strong>{{ language.t('settings.nlcAutoTranslate') }}</strong>
              <small>{{ language.t(currentNlcPlan === 'personal' ? 'settings.nlcAutoTranslateHint' : 'nlcPlans.autoTranslateHint') }}</small>
            </span>
            <input
              type="checkbox"
              :checked="nlcStatus.enabled"
              :disabled="nlcLoading||nlcSaving||!nlcServiceEnabled||Boolean(nlcRestrictionCode)"
              @change="changeNlcEnabled($event.target.checked)"
            >
            <i aria-hidden="true"></i>
          </label>

          <div v-if="nlcRestrictionCode" class="settings-nlc-restriction-inline" role="alert">
            <span>{{ language.t(nlcRestrictionCode === 'NLC_ADMIN_BANNED' ? 'settings.nlcBannedPrefix' : 'settings.nlcStoppedPrefix') }}</span>
            <button type="button" @click="openOfficialSupport">{{ language.t('settings.officialSupport') }}</button>
          </div>

          <p v-if="!nlcLoading&&!nlcServiceEnabled" class="settings-nlc-unavailable">{{ language.t('settings.nlcUnavailable') }}</p>

          <section v-if="nlcStatus.enabled" class="settings-nlc-trial">
            <div>
              <span>{{ language.t(currentNlcPlan === 'personal' ? 'settings.nlcFreeTrial' : 'nlcPlans.current') }}</span>
              <strong>{{ currentNlcPlan === 'personal' ? language.t('settings.nlcTrialDays', { days: nlcStatus.trial_total_days }) : language.t(`nlcPlans.${currentNlcPlan}`) }}</strong>
            </div>
            <div>
              <span>{{ language.t('settings.nlcRemaining') }}</span>
              <strong>{{ formattedNlcRemaining }}</strong>
              <small>{{ language.t('settings.nlcMinutes') }}</small>
            </div>
            <small v-if="currentNlcPlan === 'personal'" class="settings-nlc-countdown-hint">{{ language.t('settings.nlcCountdownHint') }}</small>
            <small v-if="currentNlcPlan !== 'personal'" class="settings-nlc-countdown-hint">{{ language.t('nlcPlans.startsAt') }}：{{ formatNlcPlanDate(nlcStatus.plan_started_at) }}<br>{{ language.t('nlcPlans.expiresAt') }}：{{ formatNlcPlanDate(nlcStatus.plan_ends_at) }}</small>
            <p v-if="nlcStatus.subscription_required">{{ language.t('translation.subscriptionRequired') }}</p>
          </section>

          <label class="settings-nlc-language">
            <span>{{ language.t('settings.nlcLanguage') }}</span>
            <select :value="language.current" @change="language.setLanguage($event.target.value)">
              <option v-for="option in languageOptions" :key="option.key" :value="option.key">{{ option.native }}</option>
            </select>
            <small>{{ language.t('settings.nlcLanguageHint') }}</small>
          </label>

          <div v-if="settingsNotice||settingsError" class="settings-nlc-feedback" :class="{error:settingsError}" :role="settingsError?'alert':'status'">
            <span>{{ language.t(settingsError||settingsNotice) }}</span>
            <button type="button" :aria-label="language.t('common.close')" @click="clearSettingsFeedback">×</button>
          </div>
          </template>
        </section>

        <section v-else-if="settingsSection === 'nlc-plans'" class="settings-support-panel settings-nlc-plans">
          <header>
            <button type="button" class="settings-support-back" :aria-label="language.t('nlcPlans.back')" @click="selectSettingsSection('nlc')">‹</button>
            <div><h3>{{ language.t('nlcPlans.title') }}</h3></div>
          </header>
          <article v-for="plan in nlcPlans" :key="plan" class="settings-nlc-plan-card">
            <h4>{{ language.t(`nlcPlans.${plan}`) }}<small v-if="plan === currentNlcPlan">{{ language.t('nlcPlans.current') }}</small></h4>
            <p>{{ language.t(`nlcPlans.${plan}Intro`) }}</p>
            <footer v-if="plan !== 'personal'">
              <button type="button" class="primary-inline-action" @click="openOfficialSupport">{{ language.t('nlcPlans.subscribe') }}</button>
              <button type="button" class="settings-nlc-renew" @click="openOfficialSupport">{{ language.t('nlcPlans.renew') }}</button>
            </footer>
          </article>
        </section>

        <section v-else-if="settingsSection === 'nlc-support'" class="settings-support-panel">
          <header>
            <button type="button" class="settings-support-back" @click="selectSettingsSection('nlc')">‹</button>
            <div><h3>{{ language.t('settings.supportTitle') }}</h3><p>{{ language.t('settings.supportIntro') }}</p></div>
          </header>
          <p v-if="supportLoading" class="settings-support-empty">{{ language.t('settings.supportLoading') }}</p>
          <p v-else-if="supportError" class="settings-support-empty error">{{ language.t(supportError) }}</p>
          <p v-else-if="!supportStaff.length" class="settings-support-empty">{{ language.t('settings.supportEmpty') }}</p>
          <div v-else class="settings-support-list">
            <button v-for="staff in supportStaff" :key="staff.user_id" type="button" @click="chatWithSupport(staff)">
              <img v-if="staff.face_url" :src="staff.face_url" alt=""><span v-else class="settings-support-avatar">{{ supportAvatar(staff) }}</span>
              <span class="settings-support-copy"><strong>{{ staff.service_name }}</strong><small>{{ staff.account || staff.user_id }}</small></span>
              <span class="settings-support-presence" :class="{ online: staff.online }"><i></i>{{ language.t(staff.online ? 'settings.supportOnline' : 'settings.supportOffline') }}</span>
            </button>
          </div>
        </section>

        <WalletPanel v-else-if="settingsSection==='wallet'"/>
        <AccountDetailsPanel v-else-if="settingsSection==='account-details'"/>
        <NotificationPanel v-else-if="settingsSection==='notifications'"/>
        <AboutPanel v-else-if="settingsSection==='about'"/>
        <section v-else class="settings-placeholder">
          <h3>{{ activeSettingsItem?.label || language.t('settings.title') }}</h3>
          <p>{{ language.t('settings.comingSoonSentence') }}</p>
        </section>
        </div>
      </div>
    </section>
  </div>

  <div v-if="profileOpen" class="modal-backdrop" @click.self="closeProfileEditor">
    <form class="modal-panel profile-edit-panel" @submit.prevent="saveProfile">
      <header>
        <strong>{{ language.t('profile.title') }}</strong>
        <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeProfileEditor">×</button>
      </header>

      <div class="profile-avatar-field">
        <button
          type="button"
          class="profile-avatar-preview"
          :class="{ clickable: currentAvatarUrl }"
          :title="language.t('profile.previewAvatar')"
          @click="openAvatarPreview"
        >
          <img v-if="currentAvatarUrl" :src="currentAvatarUrl" alt="">
          <span v-else>{{ initials }}</span>
        </button>
        <button type="button" class="primary-inline-action profile-avatar-change-button" @click="choosePortraitFile">{{ language.t('profile.changeAvatar') }}</button>
        <input ref="portraitInput" type="file" accept="image/*" @change="selectPortrait">
      </div>

      <label>
        <span>{{ language.t('profile.nickname') }}</span>
        <input v-model.trim="profileDraft.displayName" maxlength="64" autocomplete="off">
      </label>

      <label>
        <span>{{ language.t('profile.userId') }}</span>
        <input :value="session.userId" readonly>
      </label>

      <div class="modal-actions">
        <button type="button" class="ghost-inline-action" @click="closeProfileEditor">{{ language.t('common.cancel') }}</button>
        <span v-if="profileNotice" class="inline-save-status">{{ profileNotice }}</span>
        <span v-else-if="profileError" class="inline-save-status error">{{ language.error(profileError) }}</span>
        <button class="primary-inline-action" :disabled="profileSaving || !profileDraft.displayName">
          {{ profileSaving ? language.t('common.saving') : language.t('common.save') }}
        </button>
      </div>
    </form>
  </div>

  <div v-if="avatarPreviewViewer.open" class="modal-backdrop avatar-preview-backdrop" @click.self="closeAvatarPreview">
    <section class="avatar-preview-dialog">
      <header>
        <strong>{{ language.t('profile.avatarPreview') }}</strong>
        <div class="avatar-preview-actions">
          <button type="button" :title="language.t('profile.zoomOut')" @click="zoomAvatarPreview(1 / 1.16)">−</button>
          <button type="button" :title="language.t('profile.zoomIn')" @click="zoomAvatarPreview(1.16)">＋</button>
          <button type="button" :title="language.t('profile.resetZoom')" @click="resetAvatarPreview">1:1</button>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeAvatarPreview">×</button>
        </div>
      </header>
      <div
        class="avatar-preview-stage"
        :class="{ dragging: avatarPreviewViewer.dragging }"
        @wheel.prevent="handleAvatarPreviewWheel"
        @pointerdown.prevent="startAvatarPreviewDrag"
        @pointermove.prevent="moveAvatarPreviewDrag"
        @pointerup.prevent="stopAvatarPreviewDrag"
        @pointerleave.prevent="stopAvatarPreviewDrag"
      >
        <img
          :src="avatarPreviewViewer.url"
          :alt="language.t('profile.avatarPreview')"
          :style="avatarPreviewStyle"
          draggable="false"
        >
      </div>
    </section>
  </div>

  <div v-if="avatarCropper.open" class="modal-backdrop avatar-cropper-backdrop" @click.self="closeAvatarCropper">
    <section class="modal-panel avatar-cropper-panel">
      <header>
        <strong>{{ language.t('profile.cropAvatar') }}</strong>
        <div class="avatar-cropper-header-actions">
          <button type="button" class="primary-inline-action" :disabled="avatarCropper.busy" @click="choosePortraitFile">{{ language.t('profile.upload') }}</button>
          <button type="button" class="icon-button" :title="language.t('common.close')" @click="closeAvatarCropper">×</button>
        </div>
      </header>
      <div class="avatar-cropper-layout">
        <div
          class="avatar-cropper-stage"
          :class="{ dragging: avatarCropper.dragging }"
          @wheel.prevent="handleCropperWheel"
          @pointerdown.prevent="startCropperDrag"
          @pointermove.prevent="moveCropperDrag"
          @pointerup.prevent="stopCropperDrag"
          @pointerleave.prevent="stopCropperDrag"
          @dragover.prevent
          @drop.prevent="dropPortrait"
        >
          <canvas ref="cropperCanvas"></canvas>
          <div class="avatar-cropper-mask" aria-hidden="true"></div>
          <div class="avatar-cropper-box" aria-hidden="true"></div>
          <div v-if="!avatarCropper.loaded" class="avatar-cropper-placeholder">{{ language.t('profile.loadingImage') }}</div>
        </div>
        <aside class="avatar-cropper-controls">
          <div class="avatar-cropper-tools">
            <button type="button" :title="language.t('profile.rotateLeft')" @click="rotateCropper(-90)">↶90°</button>
            <button type="button" :title="language.t('profile.rotateRight')" @click="rotateCropper(90)">↷90°</button>
            <button type="button" :title="language.t('profile.zoomIn')" @click="zoomCropper(1.12)">＋</button>
            <button type="button" :title="language.t('profile.zoomOut')" @click="zoomCropper(1 / 1.12)">−</button>
            <button type="button" :title="language.t('profile.reset')" @click="resetCropperTransform">{{ language.t('profile.reset') }}</button>
          </div>
          <div class="avatar-cropper-preview">
            <canvas ref="cropperPreviewCanvas"></canvas>
          </div>
          <footer>
            <span v-if="avatarCropper.error" class="inline-save-status error">{{ language.error(avatarCropper.error) }}</span>
            <button type="button" class="ghost-inline-action" :disabled="avatarCropper.busy" @click="closeAvatarCropper">{{ language.t('common.cancel') }}</button>
            <button type="button" class="primary-inline-action" :disabled="avatarCropper.busy || !avatarCropper.loaded" @click="confirmAvatarCrop">
              {{ avatarCropper.busy ? language.t('profile.processing') : language.t('profile.confirmCrop') }}
            </button>
          </footer>
        </aside>
      </div>
    </section>
  </div>
</template>

<script setup>
import WalletPanel from './WalletPanel.vue';
import NotificationPanel from './NotificationPanel.vue';
import AboutPanel from './AboutPanel.vue';
import AccountDetailsPanel from './AccountDetailsPanel.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useSessionStore } from '@/stores/session';
import { useNavigationStore } from '@/stores/navigation';
import { useContactStore } from '@/stores/contacts';
import DesktopInstall from '@/components/DesktopInstall.vue';
import { languageOptions, useLanguageStore } from '@/stores/language';
import { getNlcStatus, getNlcSupportStaff, setNlcEnabled } from '@/services/messageTranslation';

const session = useSessionStore();
const navigation = useNavigationStore();
const contacts = useContactStore();
const language = useLanguageStore();
const settingsItems = computed(() => [
  { key: 'account-details', label: language.t('settings.accountDetails') },
  { key: 'notifications', label: language.t('settings.notifications') },
  { key: 'password', label: language.t('settings.changePassword') },
  { key: 'wallet', label: language.t('settings.wallet') },
  { key: 'nlc', label: language.t('settings.nlc') },
  { key: 'feedback', label: language.t('settings.feedback'), comingSoon: true },
  { key: 'about', label: language.t('settings.about') }
]);
const initials = computed(() => (session.displayName || session.userId || 'Q').slice(0, 1).toUpperCase());
const settingsOpen = ref(false);
const settingsSection = ref('');
const passwordSaving = ref(false);
const settingsNotice = ref('');
const settingsError = ref('');
const nlcLoading = ref(false);
const nlcSaving = ref(false);
const nlcServiceEnabled = ref(false);
const nlcStatus = reactive({ enabled: false, admin_status: 'active', plan: 'personal', status: 'not_started', has_access: true, trial_total_days: 7, remaining_minutes: 10080, subscription_required: false });
const nlcPlans = ['personal', 'professional', 'enterprise'];
const currentNlcPlan = computed(() => nlcPlans.includes(nlcStatus.plan) ? nlcStatus.plan : 'personal');
const nlcRestrictionError = ref('');
const nlcRestrictionCode = computed(() => {
  if (nlcStatus.admin_status === 'banned') return 'NLC_ADMIN_BANNED';
  if (nlcStatus.admin_status === 'stopped') return 'NLC_ADMIN_STOPPED';
  return nlcRestrictionError.value;
});
const supportStaff = ref([]);
const supportLoading = ref(false);
const supportError = ref('');
let nlcRefreshTimer = 0;
const profileOpen = ref(false);
const profileSaving = ref(false);
const profileNotice = ref('');
const profileError = ref('');
const profileFile = ref(null);
const profilePreview = ref('');
const portraitInput = ref(null);
const cropperCanvas = ref(null);
const cropperPreviewCanvas = ref(null);
const profileDraft = ref({
  displayName: ''
});
const passwordDraft = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
});
const currentAvatarUrl = computed(() => profilePreview.value || session.portrait || '');
const activeSettingsItem = computed(() => settingsItems.value.find(item => item.key === settingsSection.value) || settingsItems.value[0]);
function formatNlcPlanDate(value) { if (!value) return '—'; const date = new Date(value.replace(' ', 'T') + 'Z'); return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat(language.current.replace('_', '-'), { dateStyle: 'medium', timeStyle: 'short' }).format(date); }
const formattedNlcRemaining = computed(() => new Intl.NumberFormat(language.current.replace('_', '-')).format(Math.max(0, Number(nlcStatus.remaining_minutes) || 0)));
const avatarPreviewViewer = reactive({
  open: false,
  url: '',
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  dragging: false,
  dragStartX: 0,
  dragStartY: 0,
  dragOffsetX: 0,
  dragOffsetY: 0
});
const avatarCropper = reactive({
  open: false,
  loaded: false,
  busy: false,
  error: '',
  objectUrl: '',
  fileName: 'avatar.jpg',
  image: null,
  baseScale: 1,
  scale: 1,
  rotation: 0,
  offsetX: 0,
  offsetY: 0,
  dragging: false,
  dragStartX: 0,
  dragStartY: 0,
  dragOffsetX: 0,
  dragOffsetY: 0
});
const avatarPreviewStyle = computed(() => ({
  transform: `translate3d(${avatarPreviewViewer.offsetX}px, ${avatarPreviewViewer.offsetY}px, 0) scale(${avatarPreviewViewer.scale})`
}));

const CROP_OUTPUT_SIZE = 512;

onMounted(() => {
  session.refreshProfile();
  window.addEventListener('resize', drawAvatarCropper);
});

onBeforeUnmount(() => {
  window.clearInterval(nlcRefreshTimer);
  revokeProfilePreview();
  closeAvatarCropper();
  window.removeEventListener('resize', drawAvatarCropper);
});

function showContacts() {
  navigation.showContacts();
  contacts.refreshContacts();
  contacts.refreshFriendApplications({ force: true });
}

function clearProfileFeedback() {
  profileNotice.value = '';
  profileError.value = '';
}

function clearSettingsFeedback() {
  settingsNotice.value = '';
  settingsError.value = '';
}

function resetPasswordDraft() {
  passwordDraft.oldPassword = '';
  passwordDraft.newPassword = '';
  passwordDraft.confirmPassword = '';
}

function openSettings() {
  window.clearInterval(nlcRefreshTimer);
  settingsOpen.value = true;
  settingsSection.value = '';
  clearSettingsFeedback();
}

function applyNlcStatus(payload = {}) {
  nlcServiceEnabled.value = Boolean(payload.service_enabled);
  const status = payload.nlc || {};
  Object.assign(nlcStatus, status, { subscription_required: Boolean(status.enabled && !status.has_access) });
  if (status.admin_status === 'active') nlcRestrictionError.value = '';
}

async function loadNlcStatus({ quiet = false } = {}) {
  if (!session.token) return;
  if (!quiet) nlcLoading.value = true;
  try {
    applyNlcStatus(await getNlcStatus({ token: session.token }));
  } catch (error) {
    if (!quiet) settingsError.value = 'settings.nlcLoadFailed';
  } finally {
    if (!quiet) nlcLoading.value = false;
  }
}

function startNlcRefresh() {
  window.clearInterval(nlcRefreshTimer);
  nlcRefreshTimer = window.setInterval(() => {
    if (settingsOpen.value && settingsSection.value === 'nlc') loadNlcStatus({ quiet: true });
  }, 60000);
}

async function changeNlcEnabled(enabled) {
  nlcSaving.value = true;
  clearSettingsFeedback();
  try {
    const payload = await setNlcEnabled({ token: session.token, enabled });
    applyNlcStatus(payload);
    settingsNotice.value = enabled ? 'settings.nlcEnabled' : 'settings.nlcDisabled';
    window.dispatchEvent(new CustomEvent('jc-nlc-preference-changed', { detail: { enabled, payload } }));
  } catch (error) {
    if (['NLC_ADMIN_STOPPED', 'NLC_ADMIN_BANNED'].includes(error.code)) {
      nlcRestrictionError.value = error.code;
    } else {
      settingsError.value = 'settings.nlcSaveFailed';
    }
    await loadNlcStatus({ quiet: true });
  } finally {
    nlcSaving.value = false;
  }
}

function supportAvatar(staff) {
  return String(staff.service_name || staff.nickname || staff.account || '?').slice(0, 1).toUpperCase();
}

async function loadOfficialSupport() {
  supportLoading.value = true;
  supportError.value = '';
  try {
    supportStaff.value = await getNlcSupportStaff({ token: session.token });
  } catch (error) {
    supportStaff.value = [];
    supportError.value = 'settings.supportLoadFailed';
  } finally {
    supportLoading.value = false;
  }
}

function openOfficialSupport() {
  nlcRestrictionError.value = '';
  settingsOpen.value = true;
  settingsSection.value = 'nlc-support';
  window.clearInterval(nlcRefreshTimer);
  loadOfficialSupport();
}

function chatWithSupport(staff) {
  const userID = String(staff?.user_id || '').trim();
  if (!userID) return;
  const title = staff.service_name || staff.nickname || staff.account || userID;
  settingsOpen.value = false;
  contacts.openChat({
    id: userID,
    uid: userID,
    title,
    subtitle: staff.account || userID,
    portrait: staff.face_url || '',
    avatarText: supportAvatar(staff),
    alias: '',
    rawUser: { displayName: title, name: staff.nickname || title, account: staff.account || '', portrait: staff.face_url || '' }
  });
}

function closeSettings() {
  if (passwordSaving.value || nlcSaving.value) {
    return;
  }
  settingsOpen.value = false;
  nlcRestrictionError.value = '';
  window.clearInterval(nlcRefreshTimer);
  clearSettingsFeedback();
  resetPasswordDraft();
}

function selectSettingsSection(section) {
  settingsSection.value = section;
  clearSettingsFeedback();
  if (section === 'nlc') {
    loadNlcStatus();
    startNlcRefresh();
  } else {
    window.clearInterval(nlcRefreshTimer);
    if (section === 'nlc-support') loadOfficialSupport();
  }
}

function showSettingsMenu() {
  settingsSection.value = '';
  window.clearInterval(nlcRefreshTimer);
  clearSettingsFeedback();
}

function revokeProfilePreview() {
  if (profilePreview.value) {
    URL.revokeObjectURL(profilePreview.value);
    profilePreview.value = '';
  }
}

function revokeCropperObjectUrl() {
  if (avatarCropper.objectUrl) {
    URL.revokeObjectURL(avatarCropper.objectUrl);
    avatarCropper.objectUrl = '';
  }
}

async function openProfileEditor() {
  profileOpen.value = true;
  clearProfileFeedback();
  profileDraft.value.displayName = session.displayName || session.userId || '';
  const profile = await session.refreshProfile();
  if (profileOpen.value && profile) {
    profileDraft.value.displayName = session.displayName || session.userId || '';
  }
}

function closeProfileEditor() {
  profileOpen.value = false;
  profileFile.value = null;
  clearProfileFeedback();
  revokeProfilePreview();
  closeAvatarPreview();
  closeAvatarCropper();
}

function selectPortrait(event) {
  const file = event.target.files?.[0] || null;
  event.target.value = '';
  clearProfileFeedback();
  if (file) {
    openAvatarCropper(file);
  }
}

function choosePortraitFile() {
  portraitInput.value?.click();
}

function openAvatarPreview() {
  if (!currentAvatarUrl.value) {
    return;
  }
  avatarPreviewViewer.open = true;
  avatarPreviewViewer.url = currentAvatarUrl.value;
  resetAvatarPreview();
}

function closeAvatarPreview() {
  avatarPreviewViewer.open = false;
  avatarPreviewViewer.dragging = false;
}

function resetAvatarPreview() {
  avatarPreviewViewer.scale = 1;
  avatarPreviewViewer.offsetX = 0;
  avatarPreviewViewer.offsetY = 0;
}

function zoomAvatarPreview(factor) {
  avatarPreviewViewer.scale = Math.max(0.5, Math.min(6, Number((avatarPreviewViewer.scale * factor).toFixed(2))));
  if (avatarPreviewViewer.scale <= 1) {
    avatarPreviewViewer.offsetX = 0;
    avatarPreviewViewer.offsetY = 0;
  }
}

function handleAvatarPreviewWheel(event) {
  zoomAvatarPreview(event.deltaY < 0 ? 1.12 : 1 / 1.12);
}

function startAvatarPreviewDrag(event) {
  if (avatarPreviewViewer.scale <= 1) {
    return;
  }
  avatarPreviewViewer.dragging = true;
  avatarPreviewViewer.dragStartX = event.clientX;
  avatarPreviewViewer.dragStartY = event.clientY;
  avatarPreviewViewer.dragOffsetX = avatarPreviewViewer.offsetX;
  avatarPreviewViewer.dragOffsetY = avatarPreviewViewer.offsetY;
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function moveAvatarPreviewDrag(event) {
  if (!avatarPreviewViewer.dragging) {
    return;
  }
  avatarPreviewViewer.offsetX = avatarPreviewViewer.dragOffsetX + event.clientX - avatarPreviewViewer.dragStartX;
  avatarPreviewViewer.offsetY = avatarPreviewViewer.dragOffsetY + event.clientY - avatarPreviewViewer.dragStartY;
}

function stopAvatarPreviewDrag() {
  avatarPreviewViewer.dragging = false;
}

function dropPortrait(event) {
  const file = Array.from(event.dataTransfer?.files || []).find(item => item.type.startsWith('image/'));
  if (file) {
    clearProfileFeedback();
    openAvatarCropper(file);
  }
}

function cropperStageSize() {
  const canvas = cropperCanvas.value;
  const rect = canvas?.getBoundingClientRect();
  const width = Math.max(280, Math.round(rect?.width || 360));
  const height = Math.max(280, Math.round(rect?.height || 360));
  return { width, height };
}

function cropperBoxSize() {
  const { width, height } = cropperStageSize();
  return Math.round(Math.min(width, height) * 0.74);
}

function rotatedImageSize() {
  const image = avatarCropper.image;
  if (!image) {
    return { width: 1, height: 1 };
  }
  const quarterTurn = Math.abs(avatarCropper.rotation % 180) === 90;
  return {
    width: quarterTurn ? image.naturalHeight : image.naturalWidth,
    height: quarterTurn ? image.naturalWidth : image.naturalHeight
  };
}

function clampCropperOffset() {
  const cropSize = cropperBoxSize();
  const rotated = rotatedImageSize();
  const scaledWidth = rotated.width * avatarCropper.scale;
  const scaledHeight = rotated.height * avatarCropper.scale;
  const maxX = Math.max(0, (scaledWidth - cropSize) / 2);
  const maxY = Math.max(0, (scaledHeight - cropSize) / 2);
  avatarCropper.offsetX = Math.max(-maxX, Math.min(maxX, avatarCropper.offsetX));
  avatarCropper.offsetY = Math.max(-maxY, Math.min(maxY, avatarCropper.offsetY));
}

function updateCropperBaseScale() {
  const cropSize = cropperBoxSize();
  const rotated = rotatedImageSize();
  avatarCropper.baseScale = Math.max(cropSize / rotated.width, cropSize / rotated.height);
  avatarCropper.scale = Math.max(avatarCropper.baseScale, avatarCropper.scale || avatarCropper.baseScale);
}

function drawCanvasImage(canvas, width, height, outputScale = 1, cropOnly = false) {
  const image = avatarCropper.image;
  if (!canvas || !image) {
    return;
  }

  const dpr = cropOnly ? 1 : (window.devicePixelRatio || 1);
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const context = canvas.getContext('2d');
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);
  context.fillStyle = '#eef4f7';
  context.fillRect(0, 0, width, height);
  context.save();
  context.translate(width / 2 + avatarCropper.offsetX * outputScale, height / 2 + avatarCropper.offsetY * outputScale);
  context.rotate((avatarCropper.rotation * Math.PI) / 180);
  context.scale(avatarCropper.scale * outputScale, avatarCropper.scale * outputScale);
  context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);
  context.restore();

  if (!cropOnly) {
    const cropSize = cropperBoxSize();
    context.save();
    context.fillStyle = 'rgba(10, 18, 30, 0.42)';
    context.beginPath();
    context.rect(0, 0, width, height);
    context.rect((width - cropSize) / 2, (height - cropSize) / 2, cropSize, cropSize);
    context.fill('evenodd');
    context.restore();
  }
}

function drawAvatarCropper() {
  if (!avatarCropper.open || !avatarCropper.loaded) {
    return;
  }
  clampCropperOffset();
  const { width, height } = cropperStageSize();
  drawCanvasImage(cropperCanvas.value, width, height);
  const previewCanvas = cropperPreviewCanvas.value;
  if (previewCanvas) {
    drawCanvasImage(previewCanvas, 108, 108, 108 / cropperBoxSize(), true);
  }
}

function resetCropperTransform() {
  if (!avatarCropper.image) {
    return;
  }
  avatarCropper.rotation = 0;
  avatarCropper.offsetX = 0;
  avatarCropper.offsetY = 0;
  updateCropperBaseScale();
  avatarCropper.scale = avatarCropper.baseScale;
  drawAvatarCropper();
}

function openAvatarCropper(file) {
  if (!file.type.startsWith('image/')) {
    profileError.value = '请选择图片文件';
    return;
  }
  if (file.size > 8 * 1024 * 1024) {
    profileError.value = '头像图片不能超过8MB';
    return;
  }

  closeAvatarCropper();
  avatarCropper.open = true;
  avatarCropper.loaded = false;
  avatarCropper.error = '';
  avatarCropper.fileName = file.name || 'avatar.jpg';
  avatarCropper.objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    avatarCropper.image = image;
    avatarCropper.loaded = true;
    nextTick(resetCropperTransform);
  };
  image.onerror = () => {
    avatarCropper.error = '图片加载失败';
    avatarCropper.loaded = false;
  };
  image.src = avatarCropper.objectUrl;
}

function closeAvatarCropper() {
  avatarCropper.open = false;
  avatarCropper.loaded = false;
  avatarCropper.busy = false;
  avatarCropper.error = '';
  avatarCropper.image = null;
  avatarCropper.dragging = false;
  revokeCropperObjectUrl();
}

function zoomCropper(factor) {
  if (!avatarCropper.loaded) {
    return;
  }
  updateCropperBaseScale();
  avatarCropper.scale = Math.max(avatarCropper.baseScale, Math.min(avatarCropper.baseScale * 6, avatarCropper.scale * factor));
  drawAvatarCropper();
}

function rotateCropper(degrees) {
  if (!avatarCropper.loaded) {
    return;
  }
  avatarCropper.rotation = (avatarCropper.rotation + degrees + 360) % 360;
  updateCropperBaseScale();
  clampCropperOffset();
  drawAvatarCropper();
}

function handleCropperWheel(event) {
  zoomCropper(event.deltaY < 0 ? 1.08 : 1 / 1.08);
}

function startCropperDrag(event) {
  if (!avatarCropper.loaded) {
    return;
  }
  avatarCropper.dragging = true;
  avatarCropper.dragStartX = event.clientX;
  avatarCropper.dragStartY = event.clientY;
  avatarCropper.dragOffsetX = avatarCropper.offsetX;
  avatarCropper.dragOffsetY = avatarCropper.offsetY;
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function moveCropperDrag(event) {
  if (!avatarCropper.dragging) {
    return;
  }
  avatarCropper.offsetX = avatarCropper.dragOffsetX + event.clientX - avatarCropper.dragStartX;
  avatarCropper.offsetY = avatarCropper.dragOffsetY + event.clientY - avatarCropper.dragStartY;
  drawAvatarCropper();
}

function stopCropperDrag() {
  avatarCropper.dragging = false;
}

function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('头像裁切失败'));
      }
    }, type, quality);
  });
}

async function confirmAvatarCrop() {
  if (!avatarCropper.loaded || avatarCropper.busy) {
    return;
  }
  avatarCropper.busy = true;
  avatarCropper.error = '';
  try {
    const outputCanvas = document.createElement('canvas');
    drawCanvasImage(outputCanvas, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE / cropperBoxSize(), true);
    const blob = await canvasToBlob(outputCanvas);
    const fileName = avatarCropper.fileName.replace(/\.[^.]+$/, '') || 'avatar';
    const croppedFile = new File([blob], `${fileName}.jpg`, { type: 'image/jpeg' });
    profileFile.value = croppedFile;
    revokeProfilePreview();
    profilePreview.value = URL.createObjectURL(croppedFile);
    closeAvatarCropper();
  } catch (error) {
    avatarCropper.error = error.message || '头像裁切失败';
  } finally {
    avatarCropper.busy = false;
  }
}

async function saveProfile() {
  if (profileSaving.value || !profileDraft.value.displayName) {
    return;
  }
  clearProfileFeedback();
  profileSaving.value = true;
  try {
    await session.saveProfile({
      displayName: profileDraft.value.displayName,
      portraitFile: profileFile.value
    });
    profileFile.value = null;
    revokeProfilePreview();
    profileNotice.value = '资料已保存';
    contacts.refreshContacts({ force: true });
  } catch (error) {
    profileError.value = error?.message || error?.errMsg || error?.data?.errMsg || String(error || '资料保存失败');
  } finally {
    profileSaving.value = false;
  }
}

async function savePassword() {
  if (passwordSaving.value) {
    return;
  }
  clearSettingsFeedback();
  const oldPassword = passwordDraft.oldPassword;
  const newPassword = passwordDraft.newPassword;
  const confirmPassword = passwordDraft.confirmPassword;
  if (!oldPassword || !newPassword || !confirmPassword) {
    settingsError.value = 'settings.passwordIncomplete';
    return;
  }
  if (newPassword !== confirmPassword) {
    settingsError.value = 'settings.passwordMismatch';
    return;
  }
  if (oldPassword === newPassword) {
    settingsError.value = 'settings.passwordUnchanged';
    return;
  }

  passwordSaving.value = true;
  try {
    await session.changePassword({ oldPassword, newPassword });
    resetPasswordDraft();
    settingsNotice.value = 'settings.passwordUpdated';
  } catch (error) {
    settingsError.value = 'settings.passwordFailed';
  } finally {
    passwordSaving.value = false;
  }
}

function logout() {
  closeSettings();
  contacts.clearContacts();
  session.logout();
}
</script>
